import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import {
  Board,
  HeatPump,
  OemDistribution,
} from '../../../../core/src/shared/enums';
import { useDispatch, useSelector } from '../../store/utils';
import { variablesChange } from '../../store/actions';

import { HeatPumpTypeSelect } from './heat-pump-type-select';

type Props = {
  open: boolean;
  onClose: () => void;
};

const defaultHeatPumpIpAddress = '100.100.1.10';

export const HeatPumpWizard = ({ open, onClose }: Props) => {
  const dispatch = useDispatch();
  const heatPumpTypeFromStore = useSelector((s) => s.variables.HP_TYPE);
  const heatPumpIpFromStore = useSelector((s) => s.variables.HP_ECOONE_API_IP);
  const showIpAddress = useSelector(
    (s) =>
      s.variables.BOARD === Board.Play || s.variables.BOARD === Board.SunBerry
  );
  const includeNibe = useSelector(
    (s) => get(s.variables, 'OEM_DISTRIBUTION') === OemDistribution.DZD
  );

  const [heatPumpType, setHeatPumpType] = useState<HeatPump>(HeatPump.Ecoone);
  const [heatPumpIpAddress, setHeatPumpIpAddress] = useState(
    defaultHeatPumpIpAddress
  );
  const [submitError, setSubmitError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setSubmitError(undefined);
      setHeatPumpType(heatPumpTypeFromStore ?? HeatPump.Ecoone);
      setHeatPumpIpAddress(heatPumpIpFromStore || defaultHeatPumpIpAddress);
    }
  }, [open, heatPumpTypeFromStore, heatPumpIpFromStore]);

  const handleSubmit = () => {
    if (showIpAddress && !heatPumpIpAddress.trim()) {
      setSubmitError('IP adresa je povinná');
      return;
    }

    const changes: { HP_TYPE: HeatPump; HP_ECOONE_API_IP?: string } = {
      HP_TYPE: heatPumpType,
    };

    if (showIpAddress) {
      changes.HP_ECOONE_API_IP = heatPumpIpAddress;
    }

    dispatch(variablesChange(changes));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <FormattedMessage
          id="heat_pump_wizard.title"
          defaultMessage="Nastavení tepelného čerpadla"
        />
      </DialogTitle>
      <DialogContent>
        <Stack gap={2.5} sx={{ py: 2 }}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <HeatPumpTypeSelect
            value={heatPumpType}
            onChange={setHeatPumpType}
            includeNibe={includeNibe}
          />
          {showIpAddress && (
            <>
              <TextField
                label={
                  <FormattedMessage
                    id="heat_pump_wizard.ip_address.input"
                    defaultMessage="IP adresa tepelného čerpadla"
                  />
                }
                value={heatPumpIpAddress}
                onChange={(e) => setHeatPumpIpAddress(e.target.value)}
                fullWidth
                required
                error={showIpAddress && !heatPumpIpAddress.trim()}
              />
              <Alert icon={false} color="warning">
                <FormattedMessage
                  id="heat_pump_wizard.ip_address.alert"
                  defaultMessage="Ujistěte se prosím, že se IP adresa tepelného čerpadla nebude měnit (např. když TČ využívá adresu z DHCP)."
                />
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage
            id="heat_pump_wizard.cancel"
            defaultMessage="Zrušit"
          />
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          <FormattedMessage
            id="heat_pump_wizard.save"
            defaultMessage="Uložit"
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
